import { useState } from 'react';
import { LANDING } from '@/constants/testIds';
import { BRANDS } from '@/data/business';
import { useReveal } from '@/hooks/useReveal';

const faviconUrl = (domain) =>
    domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null;

const BrandLogo = ({ brand, size = 'md' }) => {
    const [errored, setErrored] = useState(false);
    const src = brand.logo || faviconUrl(brand.domain);
    if (!src || errored) return null;
    const hasRealLogo = !!brand.logo;
    const box =
        size === 'lg'
            ? 'h-20 w-32 sm:h-24 sm:w-40'
            : 'h-14 w-24 sm:h-16 sm:w-28';
    const bgClass = brand.darkBg ? 'bg-transparent' : 'bg-white';
    return (
        <div className={`flex items-center justify-center px-3 py-2 corner-cut ${box} ${bgClass}`}>
            <img
                src={src}
                alt={`${brand.name} logo`}
                onError={() => setErrored(true)}
                onLoad={(e) => {
                    if (!hasRealLogo && e.currentTarget.naturalWidth < 24) setErrored(true);
                }}
                loading="lazy"
                className={`max-h-full max-w-full object-contain ${brand.invert ? 'invert' : ''}`}
            />
        </div>
    );
};

export const Brands = () => {
    const [ref, visible] = useReveal();
    const featured = BRANDS.filter((b) => b.highlight);
    const rest = BRANDS.filter((b) => !b.highlight);

    return (
        <section
            id="brands"
            data-testid={LANDING.brands}
            ref={ref}
            className="relative py-20 sm:py-28 border-t border-white/5 overflow-hidden"
        >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[420px] blur-orb-yellow opacity-40 pointer-events-none" />

            <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
                <div className={`${visible ? 'in-view' : ''} reveal flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 sm:mb-14`}>
                    <div>
                        <span className="section-label">04 / Marques distribuées</span>
                        <h2 className="mt-4 font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9] text-white">
                            Des marques EPI
                            <br />
                            <span className="text-outline-yellow">de référence</span>
                        </h2>
                    </div>
                    <p className="max-w-md text-base sm:text-lg text-white leading-relaxed">
                        Un sourcing exigeant chez les fabricants reconnus. Les pièces que portent vraiment les pros sur le terrain — du chantier BTP à l'atelier de précision.
                    </p>
                </div>

                {/* Featured brand */}
                {featured.map((brand) => (
                    <div
                        key={brand.name}
                        data-testid={`${LANDING.brandCard}-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`group relative corner-cut overflow-hidden bg-[#FFD500] text-black border border-[#FFD500] mb-3 sm:mb-4 ${
                            visible ? 'in-view reveal' : 'reveal'
                        }`}
                        style={{ minHeight: '300px' }}
                    >
                        <div className="absolute top-0 right-0 hazard-stripes-thin h-1.5 w-full opacity-90" />
                        <div className="relative h-full p-6 sm:p-10 grid sm:grid-cols-[1fr,auto] gap-6 items-center">
                            <div>
                                <span className="text-xs uppercase tracking-[0.25em] font-bold text-black/85">
                                    {brand.tag}
                                </span>
                                <div className="font-display uppercase leading-none text-5xl sm:text-7xl lg:text-8xl mt-3">
                                    {brand.name}
                                </div>
                                <p className="mt-5 text-base sm:text-lg text-black max-w-md leading-relaxed font-medium">
                                    Coup de cœur historique : <strong>Blaklader</strong>, fabriquant suédois depuis 1959. Vêtements de travail premium d'une robustesse inégalée — pantalons cargo, vestes hiver, polos techniques. La référence absolue du BTP exigeant.
                                </p>
                            </div>
                            <div className="hidden sm:flex flex-col items-center justify-center bg-black/15 p-6 corner-cut">
                                <BrandLogo brand={brand} size="lg" />
                            </div>
                        </div>
                    </div>
                ))}

                {/* Grid of other brands */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {rest.map((brand, i) => (
                        <div
                            key={brand.name}
                            data-testid={`${LANDING.brandCard}-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`group relative corner-cut overflow-hidden bg-[#0F0F0F] border border-white/15 hover:border-[#FFD500]/70 transition-all duration-500 min-h-[160px] ${
                                visible ? 'in-view reveal' : 'reveal'
                            }`}
                            style={{ animationDelay: `${0.05 + i * 0.05}s` }}
                        >
                            <div className="relative h-full p-4 sm:p-5 flex flex-col justify-between items-center text-center gap-3">
                                <BrandLogo brand={brand} size="md" />
                                <div>
                                    <div className="font-display uppercase text-xl sm:text-2xl text-white leading-tight">
                                        {brand.name}
                                    </div>
                                    <div className="mt-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-white/75">
                                        {brand.tag}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <p className="mt-8 text-center text-sm uppercase tracking-[0.25em] text-white/65">
                    + d'autres marques disponibles en boutique
                </p>
            </div>
        </section>
    );
};
